import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ApiChannel } from 'core/constants';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHeaders, XmStore } from 'core/services';
import { StoreAction } from 'store/actions';
import { ModelBase } from 'store/model-base';

import {
    Cms5GPlanOverview,
    CmsABTest,
    CmsAccountDataPlanCollection,
    CmsActivation,
    CmsAddonDetails,
    CmsAddonErrors,
    CmsAddonPlans,
    CmsAgentQuickpay,
    CmsBillingAdp,
    CmsBillingCredit,
    CmsBillingExchangeInstruction,
    CmsBillingLanding,
    CmsBillingPayment,
    CmsBillingStatement,
    CmsBucketSwitch,
    CmsByodExchange,
    CmsCallFromUS,
    CmsCompatibilityCheck,
    CmsContactBar,
    CmsCore,
    CmsCountryCodingMapping,
    CmsDataDetail,
    CmsDataSaver,
    CmsDeviceCancel,
    CmsDeviceReturn,
    CmsDevices,
    CmsDeviceSuspend,
    CmsDeviceUpgrade,
    CmsErrors,
    CmsEsimActivation,
    CmsEsimDownload,
    CmsExchangeOrder,
    CmsExtendedPromos,
    CmsHop,
    CmsInternational,
    CmsInternationalCalling,
    CmsInternationalTravel,
    CmsLevelMoveEligibility,
    CmsLevelMoveError,
    CmsLevelMoveInProgress,
    CmsLevelMovePending,
    CmsLevelMoveStart,
    CmsLevelMoveSuccess,
    CmsLineDataPlanCollection,
    CmsLineTransfer,
    CmsLogin,
    CmsMyAccount,
    CmsMyAccountModals,
    CmsNonXfinityCustomer,
    CmsOneTimePasscode,
    CmsOneTimePayment,
    CmsOrderDetail,
    CmsPlanSwitch,
    CmsQuickpayBreadcrumbs,
    CmsQuickpayErrors,
    CmsRetryPayment,
    CmsReturnOrder,
    CmsSettings,
    CmsSimSwap,
    CmsStatementList,
    CmsTradeInDevice,
    CmsTravelRates,
    CmsUsageDetail,
    CmsUserQuickpay,
    CmsVideoResolution,
    CmsXmbComingSoon
} from './models';
import { CmsEsim } from './models/esim';

@Injectable()
export class CmsApi {
    public static getCore(_xmStore: XmStore, http: HttpClient): Observable<CmsCore> {
        const API_GATEWAY: ApiChannel = ApiChannel.PROXY_CMS;

        const requests: Observable<object>[] = [
            http.get('/core/footer.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }),
            http.get('/core/modals.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }),
            http.get('/core/meta-data.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }),
            http.get('/core/nav.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }),
            http.get('/core/icons.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }),
            http.get('/core/carousel.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) })
        ];

        return forkJoin(requests).pipe(
            map((response: [ SiteFooter, SiteModals, SiteMetaData, SiteNav, CmsIcons, CmsCarousel ]) => {
                const [ footer, modals, metaData, nav, icons, carousel ]:
                [ SiteFooter, SiteModals, SiteMetaData, SiteNav, CmsIcons, CmsCarousel ] = response;

                return CmsCore.create<CmsCore>({ footer, modals, metaData, nav, icons, carousel });

            })
        );
    }

    public static getXmbComingSoon(_xmStore: XmStore, http: HttpClient): Observable<CmsXmbComingSoon> {
        return http.get('/pages/xmb-coming-soon.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsXmbComingSoon) => CmsXmbComingSoon.create<CmsXmbComingSoon>({ errorData: response }))
        );
    }

    public static getNonXfinityCustomerPage(_xmStore: XmStore, http: HttpClient): Observable<CmsNonXfinityCustomer> {
        return http.get('/pages/non-xfinity-customer.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNonXfinityCustomer) => CmsNonXfinityCustomer.create<CmsNonXfinityCustomer>(response))
        );
    }

    public static getLineDataPlan(_xmStore: XmStore, http: HttpClient): Observable<CmsLineDataPlanCollection> {
        return http.get('/pages/my-account/activity/line-data-plan.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLineDataPlanCollection) => CmsLineDataPlanCollection.create<CmsLineDataPlanCollection>({ plans: response }))
        );
    }

    public static getAccountDataPlan(_xmStore: XmStore, http: HttpClient): Observable<CmsAccountDataPlanCollection> {
        return http.get('/pages/my-account/activity/account-data-plan.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsAccountDataPlanCollection) => CmsAccountDataPlanCollection.create<CmsAccountDataPlanCollection>({ plans: response }))
        );
    }

    public static getPlanSwitch(_xmStore: XmStore, http: HttpClient): Observable<CmsPlanSwitch> {
        return http.get('/pages/my-account/activity/plan-switch.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsPlanSwitch) => CmsPlanSwitch.create<CmsPlanSwitch>(response))
        );
    }

    public static getBucketSwitch(_xmStore: XmStore, http: HttpClient): Observable<CmsBucketSwitch> {
        return http.get('/pages/my-account/activity/bucket-switch.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBucketSwitch) => CmsBucketSwitch.create<CmsBucketSwitch>(response))
        );
    }

    public static getErrorsPage(_xmStore: XmStore, http: HttpClient): Observable<CmsErrors> {
        return http.get('/pages/errors.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsErrors) => CmsErrors.create<CmsErrors>(response))
        );
    }

    public static getExtendedPromoData(_xmStore: XmStore, http: HttpClient): Observable<CmsExtendedPromos> {
        return http.get('/pages/my-account/billing/extended-promos.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsExtendedPromos) => CmsExtendedPromos.create<CmsExtendedPromos>(response))
        );
    }

    public static getActivationPage(_xmStore: XmStore, http: HttpClient): Observable<CmsActivation> {
        return http.get('/pages/my-account/devices/activation.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsActivation) => CmsActivation.create<CmsActivation>(response))
        );
    }

    public static getEsimActivationPage(_xmStore: XmStore, http: HttpClient): Observable<CmsEsimActivation> {
        return http.get('/pages/my-account/devices/esim-activation.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsEsimActivation) => CmsEsimActivation.create<CmsEsimActivation>(response))
        );
    }

    public static getBillingLanding(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingLanding> {
        return http.get('/pages/my-account/billing/landing.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingLanding) => CmsBillingLanding.create<CmsBillingLanding>(response))
        );
    }

    public static getBillingAdp(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingAdp> {
        return http.get('/pages/my-account/billing/adp.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingAdp) => CmsBillingAdp.create<CmsBillingAdp>(response))
        );
    }

    public static getBillingPayment(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingPayment> {
        return http.get('/pages/my-account/billing/payment.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingPayment) => CmsBillingPayment.create<CmsBillingPayment>(response))
        );
    }

    public static getBillingStatement(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingStatement> {
        return http.get('/pages/my-account/billing/statement.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingStatement) => CmsBillingStatement.create<CmsBillingStatement>(response))
        );
    }

    public static getBillingRefundCredit(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingCredit> {
        return http.get('/pages/my-account/billing/credit.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingCredit) => CmsBillingCredit.create<CmsBillingCredit>(response))
        );
    }

    public static getBillingExchangeInstruction(_xmStore: XmStore, http: HttpClient): Observable<CmsBillingExchangeInstruction> {
        return http.get('/pages/my-account/billing/exchange-instruction.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsBillingExchangeInstruction) => CmsBillingExchangeInstruction.create<CmsBillingExchangeInstruction>(response))
        );
    }

    public static getUsageDetail(_xmStore: XmStore, http: HttpClient): Observable<CmsUsageDetail> {
        return http.get('/pages/my-account/billing/usage-detail.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsUsageDetail) => CmsUsageDetail.create<CmsUsageDetail>(response))
        );
    }

    public static getReturnOrder(_xmStore: XmStore, http: HttpClient): Observable<CmsReturnOrder> {
        return http.get('/pages/my-account/billing/return-order.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsReturnOrder) => CmsReturnOrder.create<CmsReturnOrder>(response))
        );
    }

    public static getOrderDetail(_xmStore: XmStore, http: HttpClient): Observable<CmsOrderDetail> {
        return http.get('/pages/my-account/billing/order-detail.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsOrderDetail) => CmsOrderDetail.create<CmsOrderDetail>(response))
        );
    }

    public static getExchangeOrder(_xmStore: XmStore, http: HttpClient): Observable<CmsExchangeOrder> {
        return http.get('/pages/my-account/billing/exchange-order.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsExchangeOrder) => CmsExchangeOrder.create<CmsExchangeOrder>(response))
        );
    }

    public static getDevices(_xmStore: XmStore, http: HttpClient): Observable<CmsDevices> {
        return http.get('/pages/my-account/devices/detail.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDevices) => CmsDevices.create<CmsDevices>(response))
        );
    }

    public static getDeviceUpgrade(_xmStore: XmStore, http: HttpClient): Observable<CmsDeviceUpgrade> {
        return http.get('/pages/my-account/devices/device-upgrade.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDeviceUpgrade) => CmsDeviceUpgrade.create<CmsDeviceUpgrade>(response))
        );
    }

    public static getDeviceSuspend(_xmStore: XmStore, http: HttpClient): Observable<CmsDeviceSuspend> {
        return http.get('/pages/my-account/devices/suspend.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDeviceSuspend) => CmsDeviceSuspend.create<CmsDeviceSuspend>(response))
        );
    }

    public static getDeviceCancel(_xmStore: XmStore, http: HttpClient): Observable<CmsDeviceCancel> {
        return http.get('/pages/my-account/devices/cancel.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDeviceCancel) => CmsDeviceCancel.create<CmsDeviceCancel>(response))
        );
    }

    public static getDeviceEsimDownload(_xmStore: XmStore, http: HttpClient): Observable<CmsEsimDownload> {
        return http.get('/pages/my-account/devices/esim-download.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsEsimDownload) => CmsEsimDownload.create<CmsEsimDownload>(response))
        );
    }

    public static getPasscodeEntry(_xmStore: XmStore, http: HttpClient): Observable<CmsOneTimePasscode> {
        return http.get('/pages/my-account/devices/one-time-passcode.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsOneTimePasscode) => CmsOneTimePasscode.create<CmsOneTimePasscode>(response))
        );
    }

    public static getLogin(_xmStore: XmStore, http: HttpClient): Observable<CmsLogin> {
        const API_GATEWAY: ApiChannel = ApiChannel.PROXY_CMS;

        return http.get('/pages/login.json', {
            headers: buildHeaders({ apiChannel: API_GATEWAY })
        }).pipe(
            map((response: CmsLogin) => CmsLogin.create<CmsLogin>(response))
        );
    }

    public static getHop(_xmStore: XmStore, http: HttpClient): Observable<CmsHop> {
        return http.get('/core/hop.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsHop) => CmsHop.create<CmsHop>(response))
        );
    }

    public static getSettings(_xmStore: XmStore, http: HttpClient): Observable<CmsSettings> {
        return http.get('/pages/my-account/settings/settings.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsSettings) => CmsSettings.create<CmsSettings>(response))
        );
    }

    public static get5GPlanOverview(_xmStore: XmStore, http: HttpClient): Observable<Cms5GPlanOverview> {
        return http.get('/pages/my-account/shared/level-move/plan-overview.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: Cms5GPlanOverview) => Cms5GPlanOverview.create<Cms5GPlanOverview>(response))
        );
    }

    public static getLevelMoveStart(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMoveStart> {
        return http.get('/pages/my-account/shared/level-move/level-move-start.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMoveStart) => CmsLevelMoveStart.create<CmsLevelMoveStart>(response))
        );
    }

    public static getLevelMoveEligibility(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMoveEligibility> {
        return http.get('/pages/my-account/shared/level-move/eligibility.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMoveEligibility) => CmsLevelMoveEligibility.create<CmsLevelMoveEligibility>(response))
        );
    }

    public static getLevelMoveInProgress(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMoveEligibility> {
        return http.get('/pages/my-account/shared/level-move/in-progress.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMoveInProgress) => CmsLevelMoveInProgress.create<CmsLevelMoveInProgress>(response))
        );
    }

    public static getLevelMoveSuccess(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMoveSuccess> {
        return http.get('/pages/my-account/shared/level-move/success.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMoveSuccess) => CmsLevelMoveSuccess.create<CmsLevelMoveSuccess>(response))
        );
    }

    public static getLevelMovePending(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMovePending> {
        return http.get('/pages/my-account/shared/level-move/pending.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMovePending) => CmsLevelMovePending.create<CmsLevelMovePending>(response))
        );
    }

    public static getLevelMoveError(_xmStore: XmStore, http: HttpClient): Observable<CmsLevelMoveError> {
        return http.get('/pages/my-account/shared/level-move/level-move-error.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLevelMoveError) => CmsLevelMoveError.create<CmsLevelMoveError>({ errors: response }))
        );
    }

    public static getMyAccountModals(_xmStore: XmStore, http: HttpClient): Observable<CmsMyAccountModals> {
        return http.get('/pages/my-account/modals.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsMyAccountModals) => CmsMyAccountModals.create<CmsMyAccountModals>({ modals: response }))
        );
    }

    public static getMyAccount(_xmStore: XmStore, http: HttpClient): Observable<CmsMyAccount> {
        return http.get('/pages/my-account/my-account.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsMyAccount) => CmsMyAccount.create<CmsMyAccount>(response))
        );
    }

    public static getDataDetail(_xmStore: XmStore, http: HttpClient): Observable<CmsDataDetail> {
        return http.get('/pages/my-account/activity/data-detail.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDataDetail) => CmsDataDetail.create<CmsDataDetail>(response))
        );
    }

    public static getAddonPlans(_xmStore: XmStore, http: HttpClient): Observable<CmsAddonPlans> {
        return http.get('/pages/my-account/activity/addon-plans.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsAddonPlans) => CmsAddonPlans.create<CmsAddonPlans>({ plans: response }))
        );
    }

    public static getInternationalTravelData(_xmStore: XmStore, http: HttpClient): Observable<CmsInternationalTravel> {
        return http.get('/pages/my-account/activity/international-travel.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsInternationalTravel) => CmsInternationalTravel.create<CmsInternationalTravel>(response))

        );
    }

    public static getInternationalCallingData(_xmStore: XmStore, http: HttpClient): Observable<CmsInternationalCalling> {
        return http.get('/pages/my-account/activity/international-calling.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsInternationalCalling) => CmsInternationalCalling.create<CmsInternationalCalling>(response))
        );
    }

    public static getCallFromUSData(_xmStore: XmStore, http: HttpClient): Observable<CmsCallFromUS> {
        return http.get('/pages/my-account/activity/call-from-us.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsCallFromUS) => CmsCallFromUS.create<CmsCallFromUS>(response))

        );
    }

    public static getAddonDetails(_xmStore: XmStore, http: HttpClient): Observable<CmsAddonDetails> {
        return http.get('/pages/my-account/activity/addon-details.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsAddonDetails) => CmsAddonDetails.create<CmsAddonDetails>({ details: response }))
        );
    }

    public static getAddonErrors(_xmStore: XmStore, http: HttpClient): Observable<CmsAddonErrors> {
        return http.get('/pages/my-account/activity/addon-errors.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsAddonErrors) => CmsAddonErrors.create<CmsAddonErrors>(response))
        );
    }

    public static getContactBar(_xmStore: XmStore, http: HttpClient): Observable<CmsContactBar> {
        return http.get('/core/contact-bar.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsContactBar) => CmsContactBar.create<CmsContactBar>(response))
        );
    }

    public static getCmsDeviceReturn(_xmStore: XmStore, http: HttpClient): Observable<CmsDeviceReturn> {
        return http.get('/pages/my-account/devices/return.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDeviceReturn) => CmsDeviceReturn.create<CmsDeviceReturn>(response))
        );
    }

    public static getCmsUserQuickpay(_xmStore: XmStore, http: HttpClient): Observable<CmsUserQuickpay> {
        return http.get('/pages/my-account/quickpay/user-flow.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsUserQuickpay) => CmsUserQuickpay.create<CmsUserQuickpay>(response))
        );
    }

    public static getCmsAgentQuickpay(_xmStore: XmStore, http: HttpClient): Observable<CmsAgentQuickpay> {
        return http.get('/pages/my-account/quickpay/agent-flow.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsAgentQuickpay) => CmsAgentQuickpay.create<CmsAgentQuickpay>(response))
        );
    }

    public static getCmsQuickpayErrors(_xmStore: XmStore, http: HttpClient): Observable<CmsQuickpayErrors> {
        return http.get('/pages/my-account/quickpay/error.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsQuickpayErrors) => CmsQuickpayErrors.create<CmsQuickpayErrors>(response))
        );
    }

    public static getCmsQuickpayBreadcrumbs(_xmStore: XmStore, http: HttpClient): Observable<CmsQuickpayBreadcrumbs> {
        return http.get('/pages/my-account/quickpay/breadcrumbs.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsQuickpayBreadcrumbs) => CmsQuickpayBreadcrumbs.create<CmsQuickpayBreadcrumbs>(response))
        );
    }

    public static getInternational(_xmStore: XmStore, http: HttpClient): Observable<CmsInternational> {
        return http.get('/pages/my-account/international.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsInternational) => CmsInternational.create<CmsInternational>(response))
        );
    }

    public static getABTest(_xmStore: XmStore, http: HttpClient): Observable<CmsABTest> {
        return http.get('/tests/ab-test.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsABTest) => CmsABTest.create<CmsABTest>(response))
        );
    }

    public static getCmsOneTimePayment(_xmStore: XmStore, http: HttpClient): Observable<CmsOneTimePayment> {
        return http.get('/pages/my-account/billing/one-time-payment.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsOneTimePayment) => CmsOneTimePayment.create<CmsOneTimePayment>(response))
        );
    }

    public static getCmsStatementList(_xmStore: XmStore, http: HttpClient): Observable<CmsStatementList> {
        return http.get('/pages/my-account/billing/statement-list.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsStatementList) => CmsStatementList.create<CmsStatementList>(response))
        );
    }

    public static getCmsAdditionalDevicePayment(_xmStore: XmStore, http: HttpClient): Observable<CmsOneTimePayment> {
        return http.get('/pages/my-account/devices/additional-device-payment.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsOneTimePayment) => CmsOneTimePayment.create<CmsOneTimePayment>(response))
        );
    }

    public static getCmsRetryPayment(_xmStore: XmStore, http: HttpClient): Observable<CmsRetryPayment> {
        return http.get('/pages/my-account/settings/retry-payment.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsRetryPayment) => CmsRetryPayment.create<CmsRetryPayment>(response))
        );
    }

    public static getCmsDataSaver(_xmStore: XmStore, http: HttpClient): Observable<CmsDataSaver> {
        return http.get('/pages/my-account/activity/data-saver.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsDataSaver) => CmsDataSaver.create<CmsDataSaver>(response))
        );
    }

    public static getSimSwap(_xmStore: XmStore, http: HttpClient): Observable<CmsSimSwap> {
        return http.get('/pages/my-account/devices/sim-swap.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsSimSwap) => CmsSimSwap.create<CmsSimSwap>(response))
        );
    }

    public static getCmsByodExchange(_xmStore: XmStore, http: HttpClient): Observable<CmsByodExchange> {
        return http.get('/pages/my-account/devices/byod-exchange.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsByodExchange) => CmsByodExchange.create<CmsByodExchange>(response))
        );
    }

    public static getCountryCodeMapping(_xmStore: XmStore, http: HttpClient): Observable<CmsCountryCodingMapping> {
        return http.get('/pages/my-account/billing/country-code-mapping.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsCountryCodingMapping) => CmsCountryCodingMapping.create<CmsCountryCodingMapping>(response))
        );
    }

    public static getCmsTravelRates(_xmStore: XmStore, http: HttpClient): Observable<CmsTravelRates> {
        return http.get('/pages/my-account/activity/travel-rates.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsTravelRates) => CmsTravelRates.create<CmsTravelRates>(response))

        );
    }

    public static getCmsLineTransfer(_xmStore: XmStore, http: HttpClient): Observable<CmsLineTransfer> {
        return http.get('/pages/my-account/devices/line-transfer.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsLineTransfer) => CmsLineTransfer.create<CmsLineTransfer>(response))
        );
    }

    public static getCompatibilityCheck(_xmStore: XmStore, http: HttpClient): Observable<CmsCompatibilityCheck> {
        return http.get('/pages/my-account/devices/compatibility.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsCompatibilityCheck) => CmsCompatibilityCheck.create<CmsCompatibilityCheck>(response))
        );
    }

    public static getTradeInDevice(_xmStore: XmStore, http: HttpClient): Observable<CmsTradeInDevice> {
        return http.get('/pages/my-account/devices/trade-in-device.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsTradeInDevice) => CmsTradeInDevice.create<CmsTradeInDevice>({ details: response }))
        );
    }

    public static getVideoResolution(_xmStore: XmStore, http: HttpClient): Observable<CmsVideoResolution> {
        return http.get('/pages/my-account/activity/video-resolution.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsVideoResolution) => CmsVideoResolution.create<CmsVideoResolution>(response))
        );
    }

    public static getCmsEsim(_xmStore: XmStore, http: HttpClient): Observable<CmsEsim> {
        const API_GATEWAY: ApiChannel = ApiChannel.PROXY_CMS;

        return http.get('/pages/my-account/esim.json', { headers: buildHeaders({ apiChannel: API_GATEWAY }) }).pipe(
            map((response: CmsEsim) => CmsEsim.create<CmsEsim>(response))
        );
    }
}

ModelBase.fetchMapping[StoreAction.GET_ACCOUNT_DATA_PLAN] = CmsApi.getAccountDataPlan;
ModelBase.fetchMapping[StoreAction.GET_LINE_DATA_PLAN] = CmsApi.getLineDataPlan;
ModelBase.fetchMapping[StoreAction.GET_PLAN_SWITCH] = CmsApi.getPlanSwitch;
ModelBase.fetchMapping[StoreAction.GET_BUCKET_SWITCH] = CmsApi.getBucketSwitch;
ModelBase.fetchMapping[StoreAction.GET_BILLING_LANDING] = CmsApi.getBillingLanding;
ModelBase.fetchMapping[StoreAction.GET_BILLING_ADP] = CmsApi.getBillingAdp;
ModelBase.fetchMapping[StoreAction.GET_BILLING_PAYMENT] = CmsApi.getBillingPayment;
ModelBase.fetchMapping[StoreAction.GET_BILLING_STATEMENT] = CmsApi.getBillingStatement;
ModelBase.fetchMapping[StoreAction.GET_BILLING_CREDIT] = CmsApi.getBillingRefundCredit;
ModelBase.fetchMapping[StoreAction.GET_BILLING_EXCHANGE_INSTRUCTION] = CmsApi.getBillingExchangeInstruction;
ModelBase.fetchMapping[StoreAction.GET_CMS_USAGE_DETAIL] = CmsApi.getUsageDetail;
ModelBase.fetchMapping[StoreAction.GET_RETURN_ORDER] = CmsApi.getReturnOrder;
ModelBase.fetchMapping[StoreAction.GET_CMS_ORDER_DETAIL] = CmsApi.getOrderDetail;
ModelBase.fetchMapping[StoreAction.GET_EXTENDED_PROMOS] = CmsApi.getExtendedPromoData;
ModelBase.fetchMapping[StoreAction.GET_EXCHANGE_ORDER] = CmsApi.getExchangeOrder;
ModelBase.fetchMapping[StoreAction.GET_CORE] = CmsApi.getCore;
ModelBase.fetchMapping[StoreAction.GET_DEVICES] = CmsApi.getDevices;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_UPGRADE] = CmsApi.getDeviceUpgrade;
ModelBase.fetchMapping[StoreAction.GET_ERRORS] = CmsApi.getErrorsPage;
ModelBase.fetchMapping[StoreAction.GET_ACTIVATION] = CmsApi.getActivationPage;
ModelBase.fetchMapping[StoreAction.GET_ESIM_ACTIVATION] = CmsApi.getEsimActivationPage;
ModelBase.fetchMapping[StoreAction.GET_INTERNATIONAL] = CmsApi.getInternational;
ModelBase.fetchMapping[StoreAction.GET_LOGIN] = CmsApi.getLogin;
ModelBase.fetchMapping[StoreAction.GET_HOP] = CmsApi.getHop;
ModelBase.fetchMapping[StoreAction.GET_SETTINGS] = CmsApi.getSettings;
ModelBase.fetchMapping[StoreAction.GET_5G_PLAN_OVERVIEW] = CmsApi.get5GPlanOverview;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_START] = CmsApi.getLevelMoveStart;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_ELIGIBILITY_CMS] = CmsApi.getLevelMoveEligibility;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_IN_PROGRESS_CMS] = CmsApi.getLevelMoveInProgress;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_SUCCESS_CMS] = CmsApi.getLevelMoveSuccess;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_PENDING_CMS] = CmsApi.getLevelMovePending;
ModelBase.fetchMapping[StoreAction.GET_LEVEL_MOVE_ERROR_CMS] = CmsApi.getLevelMoveError;
ModelBase.fetchMapping[StoreAction.GET_DATA_DETAIL] = CmsApi.getDataDetail;
ModelBase.fetchMapping[StoreAction.GET_ADDONS_PLANS] = CmsApi.getAddonPlans;
ModelBase.fetchMapping[StoreAction.GET_CMS_INTERNATIONAL_TRAVEL] = CmsApi.getInternationalTravelData;
ModelBase.fetchMapping[StoreAction.GET_CMS_INTERNATIONAL_CALLING] = CmsApi.getInternationalCallingData;
ModelBase.fetchMapping[StoreAction.GET_CMS_CALL_FROM_US] = CmsApi.getCallFromUSData;
ModelBase.fetchMapping[StoreAction.GET_ADDONS_DETAILS] = CmsApi.getAddonDetails;
ModelBase.fetchMapping[StoreAction.GET_ADDON_ERRORS] = CmsApi.getAddonErrors;
ModelBase.fetchMapping[StoreAction.GET_MY_ACCOUNT] = CmsApi.getMyAccount;
ModelBase.fetchMapping[StoreAction.GET_MY_ACCOUNT_MODALS] = CmsApi.getMyAccountModals;
ModelBase.fetchMapping[StoreAction.GET_CONTACT_BAR] = CmsApi.getContactBar;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_SUSPEND] = CmsApi.getDeviceSuspend;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_CANCEL] = CmsApi.getDeviceCancel;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_ESIM_DOWNLOAD] = CmsApi.getDeviceEsimDownload;
ModelBase.fetchMapping[StoreAction.GET_CMS_PASSCODE_ENTRY] = CmsApi.getPasscodeEntry;
ModelBase.fetchMapping[StoreAction.GET_AB_TEST] = CmsApi.getABTest;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_RETURN] = CmsApi.getCmsDeviceReturn;
ModelBase.fetchMapping[StoreAction.GET_ONE_TIME_PAYMENT_CMS] = CmsApi.getCmsOneTimePayment;
ModelBase.fetchMapping[StoreAction.GET_CMS_STATEMENT_LIST] = CmsApi.getCmsStatementList;
ModelBase.fetchMapping[StoreAction.GET_ADDITIONAL_DEVICE_PAYMENT_CMS] = CmsApi.getCmsAdditionalDevicePayment;
ModelBase.fetchMapping[StoreAction.GET_RETRY_PAYMENT_CMS] = CmsApi.getCmsRetryPayment;
ModelBase.fetchMapping[StoreAction.GET_CMS_XMB_COMING_SOON] = CmsApi.getXmbComingSoon;
ModelBase.fetchMapping[StoreAction.GET_CMS_NON_XFINITY_CUSTOMER] = CmsApi.getNonXfinityCustomerPage;
ModelBase.fetchMapping[StoreAction.GET_CMS_DATA_SAVER] = CmsApi.getCmsDataSaver;
ModelBase.fetchMapping[StoreAction.GET_CMS_USER_QUICKPAY] = CmsApi.getCmsUserQuickpay;
ModelBase.fetchMapping[StoreAction.GET_CMS_AGENT_QUICKPAY] = CmsApi.getCmsAgentQuickpay;
ModelBase.fetchMapping[StoreAction.GET_CMS_QUICKPAY_ERRORS] = CmsApi.getCmsQuickpayErrors;
ModelBase.fetchMapping[StoreAction.GET_CMS_QUICKPAY_BREADCRUMBS] = CmsApi.getCmsQuickpayBreadcrumbs;
ModelBase.fetchMapping[StoreAction.GET_CMS_SIM_SWAP] = CmsApi.getSimSwap;
ModelBase.fetchMapping[StoreAction.GET_CMS_BYOD_EXCHANGE] = CmsApi.getCmsByodExchange;
ModelBase.fetchMapping[StoreAction.GET_CMS_COUNTRY_CODE_MAPPING] = CmsApi.getCountryCodeMapping;
ModelBase.fetchMapping[StoreAction.GET_CMS_TRAVEL_RATES] = CmsApi.getCmsTravelRates;
ModelBase.fetchMapping[StoreAction.GET_CMS_LINE_TRANSFER] = CmsApi.getCmsLineTransfer;
ModelBase.fetchMapping[StoreAction.GET_CMS_COMPATIBILITY_CHECK] = CmsApi.getCompatibilityCheck;
ModelBase.fetchMapping[StoreAction.GET_CMS_TRADE_IN_DEVICE] = CmsApi.getTradeInDevice;
ModelBase.fetchMapping[StoreAction.GET_CMS_VIDEO_RESOLUTION] = CmsApi.getVideoResolution;
ModelBase.fetchMapping[StoreAction.GET_CMS_ESIM] = CmsApi.getCmsEsim;

